// ============================================================
// KPI WIDGET COMPONENT
// ============================================================

import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import React from 'react';
import { cn } from '../../lib/utils';

type KPIColor = 'primary' | 'success' | 'warning' | 'error' | 'secondary';
type TrendDirection = 'up' | 'down' | 'neutral';

interface KPIWidgetProps {
  title: string;
  value: string;
  subtext: string;
  trend: TrendDirection;
  color: KPIColor;
  icon: React.ReactNode;
}

const COLOR_THEMES: Record<KPIColor, ColorTheme> = {
  primary: {
    bg: 'bg-[#00758d]/10',
    text: 'text-[#00758d]',
    border: 'border-[#00758d]/20',
    valueClass: 'text-slate-900',
  },
  secondary: {
    bg: 'bg-[#00303c]/10',
    text: 'text-[#00303c]',
    border: 'border-[#00303c]/20',
    valueClass: 'text-slate-900',
  },
  success: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    valueClass: 'text-emerald-950',
  },
  warning: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200',
    valueClass: 'text-amber-950',
  },
  error: {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    border: 'border-rose-200',
    valueClass: 'text-rose-950',
  },
};

interface ColorTheme {
  bg: string;
  text: string;
  border: string;
  valueClass: string;
}

export function KPIWidget({ title, value, subtext, trend, color, icon }: KPIWidgetProps) {
  const theme = COLOR_THEMES[color];
  const TrendIcon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : null;
  const trendColor = trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-rose-600' : 'text-slate-500';
  const trendPercentage = trend === 'up' ? '+12%' : trend === 'down' ? '-4%' : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden">
      <BackgroundDecoration theme={theme} />

      <div className="flex justify-between items-start mb-4 relative z-10">
        <h3 className="text-sm font-semibold text-slate-500 max-w-[70%] leading-snug">{title}</h3>
        <IconBadge icon={icon} theme={theme} />
      </div>

      <div className="relative z-10">
        <div className={cn('text-[28px] font-bold tracking-tight mb-1 font-mono', theme.valueClass)}>{value}</div>
        <TrendIndicator
          trendColor={trendColor}
          trendIcon={TrendIcon}
          trendPercentage={trendPercentage}
          subtext={subtext}
        />
      </div>
    </div>
  );
}

function BackgroundDecoration({ theme }: { theme: ColorTheme }) {
  return (
    <div
      className={cn(
        'absolute top-0 right-0 w-24 h-24 rounded-bl-full bg-gradient-to-bl from-current opacity-[0.03] transition-opacity group-hover:opacity-[0.06]',
        theme.text,
      )}
    />
  );
}

function IconBadge({ icon, theme }: { icon: React.ReactNode; theme: ColorTheme }) {
  return (
    <div className={cn('p-2 rounded-lg border', theme.bg, theme.text, theme.border)}>
      {React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
        className: 'w-5 h-5',
      })}
    </div>
  );
}

interface TrendIndicatorProps {
  trendColor: string;
  trendIcon: typeof ArrowUpRight | null;
  trendPercentage: string | null;
  subtext: string;
}

function TrendIndicator({ trendColor, trendIcon: TrendIcon, trendPercentage, subtext }: TrendIndicatorProps) {
  return (
    <div className="flex items-center gap-1.5 mt-2">
      {trendPercentage && TrendIcon && (
        <span
          className={cn('inline-flex items-center text-xs font-bold px-1.5 py-0.5 rounded-md bg-slate-50', trendColor)}
        >
          <TrendIcon className="w-3 h-3 mr-0.5 stroke-[3]" />
          {trendPercentage}
        </span>
      )}
      <span className="text-xs text-slate-400 font-medium truncate">{subtext}</span>
    </div>
  );
}
